import { Box, Loader, TableSortable, Typography } from "@saviynt/design-system";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { useSelectedRows } from "../../context/SelectedRowsContext";
import { useAssignOwnerFilter } from "../AssignOwnerFilter/AssignOwnerFilterContext";
import { getSortConfigs, tableColumns } from "./AssignOwnerTableConfig";
import { useDebounce, useInfiniteScroll } from "@repo/mfe-core/host";
import {
  TextBoldCell,
  TextCell,
} from "../../../../../../../PoliciesPageList/CellRenderer/CellRenderer";
import { EmptyState, TableSkeletonLoader } from "@repo/ui";
import { useOwnersList } from "../../../../../../../../hooks/useAssignOwners";

export default function AssignOwnerTable() {
  const intl = useIntl();
  const { selectedRows, setSelectedRows } = useSelectedRows();
  const { selectedFilters, constructFiltersPayload, searchValue } =
    useAssignOwnerFilter();
  const [columns, setColumns] = useState(tableColumns);
  const [columnSortValue, setColumnSortValue] = useState<{
    sortBy: string | null;
    sortOrder: string | null;
  }>({
    sortBy: null,
    sortOrder: null,
  });
  const filtersPayload = constructFiltersPayload(selectedFilters);

  const debouncedSearch = useDebounce(searchValue.trim(), 500);
  const debouncedFilters = useDebounce(filtersPayload, 500);

  const payload = useMemo(
    () => ({
      filters: [
        ...debouncedFilters,
        ...(searchValue
          ? [
              {
                name: "username",
                value: debouncedSearch,
                operator: "contains",
              },
            ]
          : []),
      ],
      ...(columnSortValue.sortBy &&
        columnSortValue.sortOrder && {
          sortBy: columnSortValue.sortBy,
          sortOrder: columnSortValue.sortOrder.toUpperCase(),
        }),
    }),
    [
      debouncedFilters,
      searchValue,
      debouncedSearch,
      columnSortValue.sortBy,
      columnSortValue.sortOrder,
    ],
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useOwnersList(payload);

  const tableData = useMemo(
    () => data?.pages.flatMap((page) => page.value) || [],
    [data],
  );

  const tableRows = useMemo(
    () =>
      tableData.map((row) => ({
        id: row.userkey,
        userkey: row.userkey,
        username: row.username,
        usernameCell: <TextBoldCell text={row.username} />,
        firstname: <TextCell text={row.firstname} />,
        lastname: <TextCell text={row.lastname} />,
        email: <TextCell text={row.email} />,
        managername: <TextCell text={row.managername} />,
      })),
    [tableData],
  );

  const observerRef = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const handleColumnSort = useCallback((columnKey: string, order: string) => {
    setColumnSortValue({
      sortBy:
        columnKey.toLocaleLowerCase() === "usernamecell"
          ? "username"
          : columnKey,
      sortOrder: order,
    });
  }, []);

  if (isLoading) return <TableSkeletonLoader rows={5} />;

  const totalActiveUsers = data?.pages[0]?.totalActiveUsers || 0;

  return (
    <Box>
      {tableData.length > 0 ? (
        <>
          <Typography
            kind="body3"
            desktop="body3-d"
            mobile="body3-d"
            className="py-3"
          >
            {intl.formatMessage(
              {
                defaultMessage: "{start}-{end} items of {total}",
                description:
                  "Text showing the range of displayed items out of total items in assign owner table",
                id: "tjoQjE",
              },
              { start: 1, end: tableData.length, total: totalActiveUsers },
            )}
          </Typography>
          <TableSortable
            columnConfigs={{ isDndDisabled: true }}
            columns={columns}
            setColumns={setColumns}
            data={tableRows}
            sortConfigs={getSortConfigs(intl)}
            selectedRows={selectedRows}
            onRowSelect={setSelectedRows}
            onSort={handleColumnSort}
            isVirtualizationEnabled={false}
            dataTestId="NHI-Assign-Owner-Table"
            className="NHI-Assign-Owner-Table"
          />
        </>
      ) : (
        <Box className="NHI-Dashboard-Container-Table-NoRecordFound">
          <EmptyState />
        </Box>
      )}

      {isFetchingNextPage && (
        <div className="Nhi-InventoryList-loader" data-testid="loader-loop">
          <Loader kind="loop" format="main" color="brand" />
        </div>
      )}
      <div ref={observerRef} style={{ height: "1px" }} />
    </Box>
  );
}
